import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BarChart3, Eye, EyeOff, Loader2, AlertCircle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

interface LoginFormData {
  username: string;
  password: string;
}

interface LoginResult {
  success: boolean;
  message?: string;
}

interface LocationState {
  from?: {
    pathname: string;
  };
}

const Login: React.FC = () => {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [formData, setFormData] = useState<LoginFormData>({
    username: '',
    password: ''
  });
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, token } = useAuth();

  const state = location.state as LocationState | null;
  const from = state?.from?.pathname || "/";

  // Redirect if already authenticated
  useEffect(() => {
    if (token) {
      navigate('/', { replace: true });
    }
  }, [token, navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result: LoginResult = await login(formData.username, formData.password);

      if (result.success) {
        toast.success('Connexion réussie !', {
          description: 'Vous êtes maintenant connecté au système.'
        });

        await new Promise(resolve => setTimeout(resolve, 100));

        const redirectTo = from.includes('login') ? '/' : from;
        navigate(redirectTo, { replace: true });
      } else {
        const errorMsg = result.message || 'Échec de la connexion. Veuillez vérifier vos identifiants.';
        setError(errorMsg);
        toast.error('Échec de la connexion', {
          description: errorMsg
        });
      }
    } catch (err) {
      const errorMsg = 'Une erreur est survenue. Veuillez réessayer.';
      setError(errorMsg);
      toast.error('Erreur de connexion', {
        description: errorMsg
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-slate-50 text-slate-900">
      {/* Visual Section - Left Side */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="hidden md:flex md:w-1/2 lg:w-3/5 relative overflow-hidden flex-col justify-between p-12"
      >
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="/login-hero-light.png"
            alt="Hero"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-blue-50/30 to-primary/10 backdrop-blur-[1px]" />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col h-full justify-between">
          <div className="flex items-center gap-2 group cursor-default">
            <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-primary shadow-xl shadow-primary/20 group-hover:scale-110 transition-transform duration-300">
              <BarChart3 className="h-6 w-6 text-primary-foreground" />
            </div>
            <span className="text-2xl font-bold tracking-tight text-slate-900">
              AMC Lead Dispatch
            </span>
          </div>

          <div className="space-y-6 max-w-lg">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="text-5xl font-extrabold leading-tight tracking-tight text-slate-900"
            >
              Maîtrisez votre <br />
              <span className="text-primary italic">croissance</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="text-xl text-slate-600 font-medium leading-relaxed"
            >
              La solution intelligente pour automatiser et optimiser la distribution de vos leads. Performance et clarté à chaque étape.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="flex gap-4 pt-4"
            >
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/70 border border-slate-200 shadow-sm backdrop-blur-md">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-sm font-semibold text-slate-700">Système opérationnel</span>
              </div>
            </motion.div>
          </div>

          <div className="text-slate-400 text-sm font-medium">
            © {new Date().getFullYear()} AMC Lead Dispatch. Tous droits réservés.
          </div>
        </div>
      </motion.div>

      {/* Auth Section - Right Side */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12 lg:p-24 relative overflow-hidden bg-white">
        {/* Subtle Background Glows */}
        <div className="absolute top-1/4 -right-24 w-96 h-96 bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-1/4 -left-24 w-72 h-72 bg-blue-100/20 rounded-full blur-[100px] pointer-events-none" />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="w-full max-w-md z-10"
        >
          {/* Mobile Header */}
          <div className="md:hidden flex flex-col items-center mb-8 gap-3">
            <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-primary shadow-xl shadow-primary/30">
              <BarChart3 className="h-8 w-8 text-primary-foreground" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">AMC Lead Dispatch</h2>
          </div>

          <div className="space-y-2 mb-8 text-center md:text-left">
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">Bon retour !</h1>
            <p className="text-slate-500 font-medium">Connectez-vous pour gérer vos leads.</p>
          </div>

          <Card className="bg-white border-slate-200 shadow-xl shadow-slate-200/50 rounded-2xl overflow-hidden">
            <CardContent className="p-8">
              <AnimatePresence mode="wait">
                {error && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mb-6 overflow-hidden"
                  >
                    <div className="flex items-start gap-3 p-4 text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl">
                      <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-red-500" />
                      <div>
                        <div className="font-bold text-red-800">Erreur d'authentification</div>
                        <div className="mt-1 font-medium">{error}</div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2.5">
                  <Label htmlFor="username" className="text-slate-700 font-semibold px-0.5">Nom d'utilisateur</Label>
                  <Input
                    id="username"
                    name="username"
                    type="text"
                    placeholder="votre_nom"
                    value={formData.username}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                    className="bg-slate-50 border-slate-200 focus:bg-white focus:border-primary/50 focus:ring-primary/10 transition-all h-12 rounded-xl"
                  />
                </div>

                <div className="space-y-2.5">
                  <div className="flex items-center justify-between px-0.5">
                    <Label htmlFor="password" className="text-slate-700 font-semibold">Mot de passe</Label>
                    <Button variant="link" className="p-0 h-auto text-xs font-bold text-primary hover:text-primary/80 transition-colors">
                      Oublié ?
                    </Button>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                      className="bg-slate-50 border-slate-200 focus:bg-white focus:border-primary/50 focus:ring-primary/10 transition-all h-12 rounded-xl pr-12"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-1.5 top-1.5 h-9 w-9 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={loading}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </Button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 text-base font-bold transition-all duration-300 shadow-xl shadow-primary/20 rounded-xl"
                  disabled={loading || !formData.username || !formData.password}
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Chargement...</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span>Se connecter</span>
                      <ArrowRight className="h-5 w-5" />
                    </div>
                  )}
                </Button>
              </form>

              <div className="mt-8 pt-6 border-t border-slate-100 text-center">
                <p className="text-sm text-slate-500 font-medium">
                  Pas encore de compte ?{' '}
                  <button className="text-primary font-bold hover:underline transition-all">
                    Support technique
                  </button>
                </p>
              </div>
            </CardContent>
          </Card>

          <footer className="mt-12 text-center md:text-left">
            <div className="flex flex-col md:flex-row items-center gap-3 text-slate-400 text-xs font-medium">
              <span className="font-bold px-2 py-0.5 bg-slate-100 text-slate-500 rounded-md">v2.1.0</span>
              <span className="hidden md:inline">•</span>
              <span>Propulsé par AMC Technologies</span>
              <span className="hidden md:inline">•</span>
              <span className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500/50" />
                Serveur sécurisé
              </span>
            </div>
          </footer>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;
